import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, ShieldAlert, ChevronRight } from 'lucide-react';

export interface PendingWarning {
  id: string;
  reason: string;
  created_at: string;
}

interface WarningPopupProps {
  warnings: PendingWarning[];
  onAcknowledge: (id: string) => void;
}

const apiBase = () => import.meta.env.VITE_API_URL || '/api';
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const WarningPopup: React.FC<WarningPopupProps> = ({ warnings, onAcknowledge }) => {
  const [idx, setIdx] = useState(0);
  const [acking, setAcking] = useState(false);

  if (warnings.length === 0) return null;

  const current = warnings[idx];
  const total = warnings.length;

  const handleAck = async () => {
    setAcking(true);
    try {
      await fetch(`${apiBase()}/me/warnings/${current.id}/acknowledge`, {
        method: 'POST',
        headers: authHeader(),
      });
    } catch {
      // Even if network fails, we optimistically move on
    } finally {
      setAcking(false);
      onAcknowledge(current.id);
      // Move to next if there are more
      if (idx < total - 1) {
        setIdx(prev => prev - 0); // idx stays, parent removes current warning
      }
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-bg-primary border-2 border-warning/60 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Top bar */}
        <div className="bg-warning/15 border-b border-warning/30 px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="h-5 w-5 text-warning" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-text-primary">Avertissement officiel</p>
            <p className="text-xs text-text-muted">
              De l'équipe de modération Erozion
              {total > 1 && ` · ${idx + 1} sur ${total}`}
            </p>
          </div>
          {total > 1 && (
            <span className="text-xs bg-warning/20 text-warning px-2 py-0.5 rounded-full font-bold">
              {total}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-3 bg-warning/5 border border-warning/20 rounded-xl p-4">
            <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
            <p className="text-sm text-text-primary leading-relaxed">{current.reason}</p>
          </div>

          <p className="text-xs text-text-muted">
            Reçu le {new Date(current.created_at).toLocaleDateString('fr-FR', {
              day: 'numeric', month: 'long', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </p>

          <div className="bg-bg-secondary border border-border-color rounded-lg p-3 text-xs text-text-secondary space-y-1">
            <p>Cet avertissement est inscrit dans votre dossier de modération.</p>
            <p>Des violations répétées peuvent entraîner des sanctions plus sévères.</p>
          </div>

          <button
            onClick={handleAck}
            disabled={acking}
            className="w-full btn bg-warning hover:bg-warning/80 text-black font-bold py-3 flex items-center justify-center gap-2 rounded-xl text-sm transition-colors disabled:opacity-60"
          >
            {acking
              ? <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              : <>
                  J'ai compris et j'accepte cet avertissement
                  {total > 1 && idx < total - 1 && <ChevronRight className="h-4 w-4" />}
                </>
            }
          </button>
        </div>
      </div>
    </div>
  , document.body);
};

export default WarningPopup;
